export type IAskSchema =
    | IAskTextSchema
    | IAskConsentSchema
    | IAskSelectionSchema
    | IAskMultipleChoiceSchema
    | IAskNumberSchema
    | IAskMoneySchema
    | IAskFixedSchema
    | IAskDateSchema
    | IAskPhoneSchema
    | IAskEmailSchema
    | IAskUrlSchema
    | IAskMathSchema
    | IAskShortTextSchema
    | IAskBulletSchema
    | IAskPlaceholderSchema;

/** Represents the actual Ask components that are currently in-use */
export type AskType = 'Placeholder' | 'Text' | 'ShortText' | 'Selection' | 'MultipleChoice' | 'Phone' | 'Consent';

export interface IAskCoreSchema<T> {
    readonly _id?: string;
    ask: string; // the question to ask
    type: T;
    isRequired?: boolean;
    default?: string;
    variable?: string;
    flow?: IAskFlowArraySchema[]; // criteria to ask this question
}

export interface IAskPlaceholderSchema extends IAskCoreSchema<'Placeholder'> {}

export interface IAskTextSchema extends IAskCoreSchema<'Text'> {}

export interface IAskConsentSchema extends IAskCoreSchema<'Consent'> {}

export interface IAskShortTextSchema extends IAskCoreSchema<'ShortText'> {
    // type: 'ShortText' | string;
    minimum?: number;
    maximum?: number;
}

export interface IAskFixedSchema extends IAskCoreSchema<'Fixed'> {
    // type: 'Fixed' | string;
}

export interface IAskPhoneSchema extends IAskCoreSchema<'Phone'> {
    // type: 'Phone' | string;
}

export interface IAskEmailSchema extends IAskCoreSchema<'Email'> {
    // type: 'Email' | string;
}

export interface IAskUrlSchema extends IAskCoreSchema<'URL'> {
    // type: 'URL' | string;
}

export interface IAskBulletSchema extends IAskCoreSchema<'Bullet'> {}

export interface IAskMathSchema extends IAskCoreSchema<'Math'> {
    // type: 'Math' | string;
    format?: '0' | '1' | '2' | '3' | '4' | string; // decimal points
    currency?: AskCurrencySymbol;
}

export interface IAskDateSchema extends IAskCoreSchema<'Date'> {
    // type: 'Date' | string;
    format?: 'YYYY-MM-DD' | ' MM-DD-YYYY' | 'DD-MM-YYYY' | 'MMM DD YYYY' | 'MMM' | string; // eg. YYYY-MM-DD
}

export interface IAskOptionQuestionSchema {
    options: IAskOptionsSchema[];
    isRandomized?: boolean;
}

export interface IAskSelectionSchema extends IAskCoreSchema<'Selection'>, IAskOptionQuestionSchema {}

export interface IAskMultipleChoiceSchema extends IAskCoreSchema<'MultipleChoice'>, IAskOptionQuestionSchema {}

export type AskCurrencySymbol = '$' | '€' | '£' | '¥';

export interface IAskNumberSchema extends IAskCoreSchema<'Number'> {
    // type: 'Number' | string;
    minimum?: number;
    maximum?: number;
}

export interface IAskMoneySchema extends IAskCoreSchema<'Money'> {
    // type: 'Money' | string;
    minimum?: number;
    maximum?: number;
    currency?: AskCurrencySymbol;
}

export interface IAskOptionsSchema {
    readonly _id?: string;
    display: string;
    value?: string; // if no value, then use display
    keywords?: string[];
}

export interface IAskFlowArraySchema {
    variable: string;
    operation: 'eq' | 'ne' | 'gt' | 'lt' | 'gte' | 'lte';
    value: string;
}

/** model of the data to be sent to the back end */
export interface IAskAnswerSchema {
    ask: string; // copy of the question
    answer: string;
    variable?: string;
    type?: IAskSchema['type'];
}

/** model for binding and displaying data on the client */
export interface IAskAnswerData {
    askSchema: IAskSchema;
    data: { answer: string; type: IAskSchema['type']; invalidAnswer?: boolean };
}

export type QuestionType = {
    name: string;
    value: AskType;
    icon: string;
    customColor?: string;
};

// Note that Placeholder type is NOT included here
export const SELECTABLE_QUESTION_TYPES: QuestionType[] = [
    {
        name: 'Long Text',
        value: 'Text',
        icon: 'blue bars'
    },
    {
        name: 'Short Text',
        value: 'ShortText',
        icon: 'terminal'
    },
    {
        name: 'Dropdown',
        value: 'Selection',
        icon: 'green caret square down'
    },
    {
        name: 'Multiple Choice',
        value: 'MultipleChoice',
        icon: 'purple check square'
    },
    {
        name: 'Consent Form',
        value: 'Consent',
        // #F2711C orange
        icon: 'orange clipboard check icon'
    },
    {
        name: 'Phone Number',
        value: 'Phone',
        icon: 'phone'
    },
];
